
import React, { useState, useRef, forwardRef, useImperativeHandle, useEffect } from 'react';
import styles from './BusinessStep.module.css';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import Loader2 from '../Loader2/Loader2';
import { validateField } from '../../utils/validation';
import {
  validateWebsite,
  listSiteMap
} from '../../Store/apiStore';
import filterCompanyPages from '../../utils/companyKeyboards';
import { secureStorage } from '../../utils/secureStorage';
import CustomAutoSearch from '../CustomAutocomplete/CustomAutoSearch';
import GoogleAddressInputWithDropdown from '../GoogleAddressInputWithDropdown/GoogleAddressInputWithDropdown';
import { trackEvent } from '../../lib/analytics';
// Postal-code lookup must cover the country this site actually operates in.
// Hardcoded us/ca meant an India business could not enter its own PIN code,
// which then fed a wrong location into the number search downstream.
import { supportedNumberCountries, usesVobiz } from '../../utils/apiBackend';

const MAX_SELECTED_SITEMAP_URLS = 100;
const BUSINESS_EMAIL_MAX_LENGTH = 40;

const DEFAULT_HOURS_FALLBACK = [
  "Monday: 9:00 AM – 6:00 PM",
  "Tuesday: 9:00 AM – 6:00 PM",
  "Wednesday: 9:00 AM – 6:00 PM",
  "Thursday: 9:00 AM – 6:00 PM",
  "Friday: 9:00 AM – 6:00 PM",
  "Saturday: 9:00 AM – 6:00 PM",
  "Sunday: Closed",
];

const getDefaultHoursString = () => DEFAULT_HOURS_FALLBACK.join(" | ");

const hasValidGmbHours = (hours) => {
  if (!hours) return false;
  if (Array.isArray(hours)) return hours.filter(Boolean).length > 0;
  if (typeof hours === "string") return hours.trim().length > 0;
  return false;
};

const StepTwo = forwardRef(({
  data,
  updateFields,
  showDetailedForm,
  setShowDetailedForm,
  setLoading,
  setIsProcessing,
  onRequestNoGMBConfirm,
  showListingConfirm,
  onUseListing,
}, ref) => {

  const validateEmail = (email) => {
    if (!email || email.trim() === '') return null;
    if (email.trim().length > BUSINESS_EMAIL_MAX_LENGTH) {
      return `Business email must be ${BUSINESS_EMAIL_MAX_LENGTH} characters or less`;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) return 'Please enter a valid email address';
    return null;
  };
  // Keyboard for the Zip / Postal Code field, chosen from the user's country
  // (fetched + stashed on the Details page before landing here):
  //   • US or India → numeric keyboard (digit-only zip / pin codes)
  //   • anything else (e.g. Canada) → alphanumeric keyboard
  const zipInputMode = (() => {
    const c = (sessionStorage.getItem('userCountry') || '').trim().toLowerCase();
    const numericCountries = ['us', 'usa', 'united states', 'united states of america', 'india', 'in'];
    return numericCountries.includes(c) ? 'numeric' : 'text';
  })();

  const [googleResults, setGoogleResults] = useState([]);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isUrlValid, setIsUrlValid] = useState(null);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [selectedCountry, setSelectedCountry] = useState('us');
  const [filteredPages, setFilteredPages] = useState([]);
  const [selectedSitemapCount, setSelectedSitemapCount] = useState(0);
  const [userClearedWebsite, setUserClearedWebsite] = useState(false);
  const [showNoGMBCheckbox, setShowNoGMBCheckbox] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isGMBSelected, setIsGMBSelected] = useState(false);
  const [zipTouched, setZipTouched] = useState(false); // Zip Code blur tracking
  const [nameTouched, setNameTouched] = useState(false); // Business Name blur tracking
  const [nameError, setNameError] = useState(''); // Business Name required error
  const [showZipList, setShowZipList] = useState(false); // Zip location dropdown
  // Real Google Places region predictions (replaces the old mock zip list).
  const [zipPredictions, setZipPredictions] = useState([]);
  const [zipLoading, setZipLoading] = useState(false);
  const [zipHighlight, setZipHighlight] = useState(-1); // keyboard nav index
  // True when a complete pin/postal code resolved to NO location — turns the
  // field border red and blocks Continue (via the lifted `zipInvalid` flag).
  const [zipNotFound, setZipNotFound] = useState(false);
  // How many times a complete pin/postal code has come back wrong. From the
  // 2nd wrong try onward we surface the "Enter manually" banner + scroll to it.
  const [zipWrongCount, setZipWrongCount] = useState(0);
  // Real Google Places results for the listing-confirm screen (name + zip search).
  const [listingResults, setListingResults] = useState([]);
  const [listingLoading, setListingLoading] = useState(false);
  const typingTimeout = useRef(null);
  const prevPlaceIdRef = useRef(null);
  const addressInputRef = useRef(null);
  const searchContainerRef = useRef(null);
  const dropdownRef = useRef(null);
  const zipWrapRef = useRef(null);
  const zipInputRef = useRef(null);
  const zipListRef = useRef(null);
  const zipDebounceRef = useRef(null);
  const zipSessionTokenRef = useRef(null); // Places billing session token
  const zipLatestRef = useRef(''); // latest typed code — guards stale geocode responses

  // Wait (up to ~6s) for the Google Places JS to be ready. The listing search
  // runs only after the user picks a zip + hits Continue, by which point it's
  // usually loaded — this just guards slow connections.
  const waitForGooglePlaces = () =>
    new Promise((resolve) => {
      if (window.google?.maps?.places) { resolve(true); return; }
      let attempts = 0;
      const id = setInterval(() => {
        if (window.google?.maps?.places) { clearInterval(id); resolve(true); }
        else if (++attempts >= 20) { clearInterval(id); resolve(false); }
      }, 300);
    });

  // Google Places Text Search -> top 4 raw predictions (name/address/rating).
  const placesTextSearch = (query) =>
    new Promise((resolve) => {
      const service = new window.google.maps.places.PlacesService(document.createElement('div'));
      service.textSearch({ query }, (results, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && Array.isArray(results)) {
          resolve(results.slice(0, 4));
        } else {
          resolve([]);
        }
      });
    });

  // Text Search doesn't return phone OR website -> fetch both per result via
  // a Place Details call. (Previously only the phone was fetched, which is why
  // the confirm modal's Website row showed "N/A" for the "Use this" flow.)
  const fetchListingDetails = (placeId) =>
    new Promise((resolve) => {
      const service = new window.google.maps.places.PlacesService(document.createElement('div'));
      service.getDetails(
        {
          placeId,
          fields: [
            'international_phone_number', 'formatted_phone_number', 'website',
            'address_components', 'opening_hours',
          ],
        },
        (res, status) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK && res) {
            const rawHours = res.opening_hours?.weekday_text || [];
            resolve({
              phone: res.international_phone_number || res.formatted_phone_number || '',
              website: res.website || '',
              // Same shape the autocomplete path stores (pipe-joined string),
              // so the agent prompt gets real opening hours instead of blank.
              hours: hasValidGmbHours(rawHours) ? rawHours.join(' | ') : getDefaultHoursString(),
              // Structured location — required by the number-assign page to
              // auto-fetch numbers for the business's city/state/country.
              address: extractAddressComponents(res.address_components || []),
            });
          } else {
            resolve({ phone: '', website: '', hours: getDefaultHoursString(), address: {} });
          }
        }
      );
    });

  // Search Google for "<business name> <zip location>" and keep the top 4,
  // shaped as { name, address, rating, reviews, phone, website } for the cards.
  const searchBusinessListings = async () => {
    const name = (data.displayBusinessName || '').trim();
    const loc = data.zipLocation;
    // Send city + state + zip + country so Google scopes the search to the
    // resolved location's country (US zip → US businesses).
    const locPart = loc
      ? `${loc.city} ${loc.state} ${loc.zip} ${loc.country}`.trim()
      : (data.zipCode || '').trim();
    const query = `${name} ${locPart}`.trim();
    if (!query) { setListingResults([]); return; }

    // Country to constrain results to (from the resolved zip location).
    const wantCC = (loc?.country_code || '').toUpperCase();

    setListingLoading(true);
    try {
      const ready = await waitForGooglePlaces();
      if (!ready) { setListingResults([]); return; }
      const top = await placesTextSearch(query);
      const mapped = await Promise.all(
        top.map(async (r) => {
          const details = await fetchListingDetails(r.place_id);
          const addr = details.address || {};
          return {
            place_id: r.place_id,
            name: r.name || '',
            address: r.formatted_address || '',
            rating: r.rating || '',
            reviews: r.user_ratings_total || '',
            phone: details.phone,
            website: normalizeWebsite(details.website) || '',
            hours: details.hours || '',
            // Structured location for downstream number assignment.
            city: addr.city || '',
            state: addr.state || '',
            state_code: addr.state_code || '',
            country: addr.country || '',
            country_code: addr.country_code || '',
            postal_code: addr.postal_code || '',
          };
        })
      );
      // Keep only businesses in the resolved country. Results with an unknown
      // country_code are kept (don't drop a valid business just because its
      // details lookup didn't return components).
      const filtered = wantCC
        ? mapped.filter((b) => !b.country_code || b.country_code.toUpperCase() === wantCC)
        : mapped;
      setListingResults(filtered);
    } catch (e) {
      console.error('Business listing search failed:', e);
      setListingResults([]);
    } finally {
      setListingLoading(false);
    }
  };

  // Clicking anywhere inside a .formGroup card (label / padding) focuses its
  // input — unless the click landed on something already interactive.
  const focusGroupInput = (e) => {
    if (e.target.closest('input, textarea, button, a, [role="button"]')) return;
    const group = e.target.closest(`.${styles.formGroup}`);
    if (!group) return;
    const input = group.querySelector('input:not([disabled]), textarea:not([disabled])');
    if (input) input.focus();
  };

  // ── Real location autocomplete (replaces the mock ZIP_LOCATIONS list) ──

  // One billing session token spans typing → selection, then is reset.
  const getZipSessionToken = () => {
    if (!zipSessionTokenRef.current && window.google?.maps?.places) {
      zipSessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
    }
    return zipSessionTokenRef.current;
  };

  // Fetch region (city / zip / area) predictions for the typed text.
  const fetchZipPredictions = async (text) => {
    const ready = await waitForGooglePlaces();
    if (!ready || !text.trim()) { setZipPredictions([]); setZipLoading(false); return; }
    const service = new window.google.maps.places.AutocompleteService();
    service.getPlacePredictions(
      // Whatever this site can sell a number in: ['in'] or ['us','ca'].
      { input: text, types: ['(regions)'], componentRestrictions: { country: supportedNumberCountries().map((c) => c.toLowerCase()) }, sessionToken: getZipSessionToken() },
      (predictions, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && Array.isArray(predictions)) {
          // Keep only postal-code predictions — drop city/area/country results
          // so the field stays zip-code only.
          const onlyZips = predictions.filter((p) => (p.types || []).includes('postal_code'));
          setZipPredictions(onlyZips.slice(0, 6));
        } else {
          setZipPredictions([]);
        }
        setZipLoading(false);
      }
    );
  };

  // Resolve a complete zip / postal code into its real location
  // (city / state / country) in the BACKGROUND — no dropdown shown. This
  // replaces the old "pick from list" step so the downstream business search
  // still gets a structured `zipLocation`.
  const resolveZipInBackground = async (code, isCanada) => {
    const ready = await waitForGooglePlaces();
    if (!ready || !window.google?.maps) { setZipLoading(false); return; }
    // Canadian postal codes geocode more reliably with the conventional space
    // (e.g. "V9N8C7" → "V9N 8C7").
    const query = isCanada && code.length === 6
      ? `${code.slice(0, 3)} ${code.slice(3)}`
      : code;
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode(
      { address: query, componentRestrictions: { country: usesVobiz() ? 'in' : isCanada ? 'ca' : 'us' } },
      (results, status) => {
        setZipLoading(false);
        // Ignore stale responses if the user kept typing (compare against the
        // latest code via a ref — `data` here is a stale render closure).
        if (zipLatestRef.current !== code) return;

        const r0 = (status === 'OK' && Array.isArray(results)) ? results[0] : null;
        const c = r0 ? extractAddressComponents(r0.address_components || []) : {};
        // Only accept a GENUINE postal-code match. For a wrong/invalid code
        // Google often returns a loose country/region-level fallback (no real
        // postal component) — we must NOT treat that as a valid US/CA location.
        const isPostalResult = (r0?.types || []).includes('postal_code');
        const gotPostal = (c.postal_code || '').replace(/\s/g, '').toUpperCase();
        const typedPostal = code.replace(/\s/g, '').toUpperCase();
        const isRealMatch = !!r0 && (isPostalResult || (gotPostal && gotPostal === typedPostal));

        if (isRealMatch) {
          setZipNotFound(false);
          updateFields({
            zipInvalid: false,
            zipLocation: {
              zip: c.postal_code || code,
              city: c.city || '',
              state: c.state || '',
              state_code: c.state_code || '',
              country: c.country || '',
              country_code: c.country_code || '',
            },
          });
        } else {
          // Wrong pin / no genuine match — flag it (red border) and mark the zip
          // invalid so the parent disables Continue. Count the wrong tries so
          // the "Enter manually" banner can appear after more than 2. DON'T
          // fabricate a country.
          setZipNotFound(true);
          setZipWrongCount((n) => n + 1);
          updateFields({ zipLocation: null, zipInvalid: true });
        }
      }
    );
  };

  // Typing in the field: enforce the zip / postal-code format. The country is
  // detected from the FIRST character (no dropdown / Google lookup anymore):
  //   • starts with a digit  → US ZIP: numeric only, max 5  (e.g. 10001)
  //   • starts with a letter → Canada postal: alphanumeric, max 6 (e.g. H3B2Y5)
  // Once a full code is entered, it is resolved to a location in the background.
  const handleZipChange = (raw) => {
    const numericZipLength = usesVobiz() ? 6 : 5;
    const stripped = raw.replace(/[^a-zA-Z0-9]/g, '');
    let cleaned;
    let isCanada = false;
    if (/^[a-zA-Z]/.test(stripped)) {
      isCanada = true;
      cleaned = stripped.toUpperCase().slice(0, 6);          // Canada — alphanumeric, max 6
    } else {
      // India PIN codes are 6 digits, US ZIPs 5. Truncating an Indian code to
      // 5 made it both wrong and permanently 'incomplete', so the lookup below
      // never fired and the business could not set its location at all.
      cleaned = stripped.replace(/[^0-9]/g, '').slice(0, numericZipLength);
    }
    zipLatestRef.current = cleaned; // track latest for the stale-response guard
    updateFields({ zipCode: cleaned, zipLocation: null, zipInvalid: false });
    setShowZipList(false); // location dropdown intentionally hidden
    setZipNotFound(false); // clear the "not found" red state while editing

    if (zipDebounceRef.current) clearTimeout(zipDebounceRef.current);
    const complete = isCanada ? cleaned.length === 6 : cleaned.length === numericZipLength;
    if (complete) {
      // GA4: user typed a complete zip / postal code — fire right away (don't
      // wait for the background Google resolution). The one-shot guard in
      // trackEvent drops repeats of the same code.
      trackEvent('business_zip_entered', {
        zip: cleaned,
        country: isCanada ? 'CA' : 'US',
      });
      setZipLoading(true);
      zipDebounceRef.current = setTimeout(() => resolveZipInBackground(cleaned, isCanada), 300);
    } else {
      setZipLoading(false);
    }
  };

  // Pick a prediction → resolve its real components (zip/city/state/country).
  const selectZipPrediction = (prediction) => {
    if (!prediction || !window.google?.maps?.places) return;
    const service = new window.google.maps.places.PlacesService(document.createElement('div'));
    service.getDetails(
      {
        placeId: prediction.place_id,
        fields: ['address_components', 'name', 'formatted_address'],
        sessionToken: getZipSessionToken(),
      },
      (res, status) => {
        zipSessionTokenRef.current = null; // end the billing session
        const sf = prediction.structured_formatting || {};
        let loc;
        if (status === window.google.maps.places.PlacesServiceStatus.OK && res) {
          const c = extractAddressComponents(res.address_components || []);
          loc = {
            zip: c.postal_code || '',
            city: c.city || sf.main_text || '',
            state: c.state || '',
            state_code: c.state_code || '',
            country: c.country || '',
            country_code: c.country_code || '',
          };
        } else {
          // Details failed → fall back to the prediction's own text.
          loc = { zip: '', city: sf.main_text || prediction.description || '', state: '', country: '' };
        }
        // Field shows the zip when we have one, else the place name.
        const fieldValue = loc.zip || loc.city || prediction.description || '';
        updateFields({ zipCode: fieldValue, zipLocation: loc });
        setShowZipList(false);
        setZipPredictions([]);
        setZipHighlight(-1);
        setZipTouched(true);
      }
    );
  };

  // Clear the picked location and reopen the field for a fresh search.
  const clearZip = () => {
    updateFields({ zipCode: '', zipLocation: null });
    setZipPredictions([]);
    setZipHighlight(-1);
    setShowZipList(true);
    zipSessionTokenRef.current = null;
    setTimeout(() => zipInputRef.current?.focus(), 0);
  };

  // Keyboard nav over the predictions (↑ / ↓ / Enter / Escape).
  const handleZipKeyDown = (e) => {
    const total = zipPredictions.length;
    switch (e.key) {
      case 'ArrowDown':
        if (!total) return;
        e.preventDefault();
        setShowZipList(true);
        setZipHighlight((p) => (p < total - 1 ? p + 1 : 0));
        break;
      case 'ArrowUp':
        if (!total) return;
        e.preventDefault();
        setShowZipList(true);
        setZipHighlight((p) => (p > 0 ? p - 1 : total - 1));
        break;
      case 'Enter':
        if (showZipList && zipHighlight >= 0 && zipPredictions[zipHighlight]) {
          // Stop the parent's global Enter handler from also firing.
          e.preventDefault();
          e.stopPropagation();
          selectZipPrediction(zipPredictions[zipHighlight]);
        }
        break;
      case 'Escape':
        setShowZipList(false);
        setZipHighlight(-1);
        break;
      default:
        break;
    }
  };

  // Close the zip list when clicking outside the field/list.
  useEffect(() => {
    const onDocClick = (e) => {
      if (zipWrapRef.current && !zipWrapRef.current.contains(e.target)) {
        setShowZipList(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  // Keep the keyboard-highlighted prediction scrolled into view.
  useEffect(() => {
    if (zipHighlight >= 0 && zipListRef.current) {
      const el = zipListRef.current.children[zipHighlight];
      if (el) el.scrollIntoView({ block: 'nearest' });
    }
  }, [zipHighlight]);

  // Clean up the debounce timer on unmount.
  useEffect(() => () => {
    if (zipDebounceRef.current) clearTimeout(zipDebounceRef.current);
  }, []);

  // From the 2nd wrong pin try onward, scroll the pin field + the
  // "Enter manually" banner into view so the user notices the manual option.
  // Same threshold as the banner (>= 2) so both fire together.
  useEffect(() => {
    if (zipWrongCount >= 2 && zipWrapRef.current) {
      const t = setTimeout(() => {
        zipWrapRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 200);
      return () => clearTimeout(t);
    }
  }, [zipWrongCount]);

  // When the listing-confirm screen opens (Continue after a zip pick), run the
  // real Google search for "<business name> <zip>" and show the top 4.
  useEffect(() => {
    if (showListingConfirm && !showDetailedForm) {
      searchBusinessListings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showListingConfirm]);

  // ── FIX: Refs for each field — auto-scroll ke liye ──────────
  const fieldRefs = {
    googleListing: useRef(null),
    businessUrl: useRef(null),
    businessName: useRef(null),
    phone: useRef(null),
    address: useRef(null),
    email: useRef(null),
  };
  // ── Error order — jis order mein scroll hona chahiye ────────
  const ERROR_SCROLL_ORDER = ['googleListing', 'businessUrl', 'businessName', 'phone', 'address', 'email'];

  // ── Address focus pe detailForm ko poora bottom tak scroll karo ──
  // Why: detailForm scrollable hai (max-height:50dvh, overflow:auto).
  // Address neeche hai aur uske niche sirf email field hai — bottom tak
  // scroll karne se address upper area mein aa jata hai aur Google
  // suggestion dropdown ke liye visible jagah ban jati hai.
  // Smooth behavior = "slow" feel.
  const scrollAddressIntoView = () => {
    const wrapper = fieldRefs.address?.current;
    if (!wrapper) return;
    // Delay: keyboard animation (~250-300ms) ke baad scroll karo.
    setTimeout(() => {
      // Nearest scrollable ancestor dhundho — detailForm yahaan.
      let p = wrapper.parentElement;
      while (p) {
        const oy = getComputedStyle(p).overflowY;
        if ((oy === 'auto' || oy === 'scroll') && p.scrollHeight > p.clientHeight) break;
        p = p.parentElement;
      }
      if (!p) return;
      p.scrollTo({ top: p.scrollHeight, behavior: 'smooth' });
    }, 300);
  };

  // ── Scroll to first error helper ────────────────────────────
  const scrollToFirstError = (newErrors) => {
    const firstErrorKey = ERROR_SCROLL_ORDER.find(key => newErrors[key]);
    if (!firstErrorKey) return;

    const ref = fieldRefs[firstErrorKey];
    const el = ref?.current;
    if (!el) return;

    // PhoneInput ke andar nested element hota hai — closest wrapper dhundho
    const target = el.closest?.('[data-field-wrapper]') || el;

    setTimeout(() => {
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // Input focus bhi karo taaki user seedha type kar sake
      const input = target.querySelector?.('input') || (el.tagName === 'INPUT' ? el : null);
      if (input) input.focus({ preventScroll: true });
    }, 100);
  };

  // ── Outside click se dropdown band karo ─────────────────────
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(e.target)) {
        setShowDropdown(false);
        setHighlightedIndex(-1);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  useEffect(() => {
    if (highlightedIndex >= 0 && dropdownRef.current) {
      const item = dropdownRef.current.children[highlightedIndex];
      if (item) item.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
  }, [highlightedIndex]);

  useEffect(() => {
    setHighlightedIndex(-1);
  }, [googleResults]);

  const handleSearchKeyDown = (e) => {
    const total = googleResults.length;
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault(); e.stopPropagation();
        if (!showDropdown && total > 0) { setShowDropdown(true); setHighlightedIndex(0); }
        else { setHighlightedIndex((prev) => prev < total - 1 ? prev + 1 : 0); }
        break;
      case "ArrowUp":
        e.preventDefault(); e.stopPropagation();
        if (!showDropdown && total > 0) { setShowDropdown(true); setHighlightedIndex(total - 1); }
        else { setHighlightedIndex((prev) => prev > 0 ? prev - 1 : total - 1); }
        break;
      case "Enter":
        if (showDropdown && highlightedIndex >= 0 && googleResults[highlightedIndex]) {
          e.preventDefault(); e.stopPropagation();
          handlePlaceSelect(googleResults[highlightedIndex]);
          setShowDropdown(false); setHighlightedIndex(-1);
        }
        break;
      case "Escape":
        setShowDropdown(false); setHighlightedIndex(-1);
        break;
      default: break;
    }
  };

  const normalizeWebsite = (raw) => {
    if (!raw) return '';
    try {
      let v = String(raw).trim();
      v = v.replace(/^(https?:\/\/)+(www\.)?/gi, '');
      v = v.split('/')[0].toLowerCase().replace(/\s+/g, '');
      if (!v) return '';
      const domainRegex = /^[\w-]+(\.[\w-]{2,})+$/i;
      if (!domainRegex.test(v)) return '';
      return 'https://' + v;
    } catch { return ''; }
  };

  const isUrlEffectivelyEmpty = (val = '') => {
    const v = val.trim().toLowerCase();
    return v === '' || v === 'https://' || v === 'http://';
  };

  const extractAddressComponents = (addressComponents) => {
    if (!Array.isArray(addressComponents)) return {};
    const getComponent = (primaryType, fallbackType = null, useShort = false) => {
      const comp = addressComponents.find(
        (c) => c.types.includes(primaryType) || (fallbackType && c.types.includes(fallbackType))
      );
      return comp ? (useShort ? comp.short_name : comp.long_name) : '';
    };
    return {
      city: getComponent('locality', 'sublocality_level_1'),
      state: getComponent('administrative_area_level_1'),
      state_code: getComponent('administrative_area_level_1', null, true),
      country: getComponent('country'),
      country_code: getComponent('country', null, true),
      postal_code: getComponent('postal_code'),
      street_number: getComponent('street_number'),
      route: getComponent('route'),
    };
  };

  useEffect(() => {
    const interval = setInterval(() => {
      if (window.google?.maps?.places) clearInterval(interval);
    }, 300);
    return () => clearInterval(interval);
  }, []);

  // Auto-trigger GMB autocomplete on first arrival with a pre-filled
  // business name (companyName from FB lead / agent context). Saves the
  // user from re-typing what we already know — they just click their
  // listing. Empty Google results are fine: dropdown simply doesn't show,
  // which itself signals "no Google match found, switch to manual entry".
  //
  // Fires ONCE per mount via didAutoSearchRef so dismissing the dropdown
  // doesn't get fought by the effect re-running on every state change.
  //
  // Note: we DON'T skip on isGMBSelected — that flag goes true whenever
  // parent prefilled `placeDetails` from agentCtx/secureStorage, which is
  // a system-prefill, not a user-confirmed pick. Showing alternative GMB
  // matches is still useful so user can verify/swap.
  const didAutoSearchRef = useRef(false);
  useEffect(() => {
    if (didAutoSearchRef.current) return;
    if (data.noGoogleListing) return;
    if (showDetailedForm) return;
    const seed = (data.displayBusinessName || '').trim();
    if (!seed) return;

    let attempts = 0;
    const maxAttempts = 30; // ~9s — Google JS sometimes loads late on slow links
    const tick = () => {
      if (didAutoSearchRef.current) return;
      if (window.google?.maps?.places) {
        didAutoSearchRef.current = true;
        console.log('[StepTwo] auto-search GMB for prefilled name:', seed);
        fetchGoogleResults(seed);
      } else if (++attempts < maxAttempts) {
        setTimeout(tick, 300);
      } else {
        console.warn('[StepTwo] Google Places JS never loaded — auto-search skipped');
      }
    };
    tick();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.displayBusinessName, data.noGoogleListing, showDetailedForm]);

  /*
   * Stale-flag reset on search view mount.
   *
   * Bug history: clicking "Enter Business Detail Manually" set both
   * `showDetailedForm=true` AND `noGoogleListing=true`. When the
   * user navigated AWAY from step 2 (e.g. tapped the step-1 dot)
   * and came back, the parent reset `showDetailedForm` to false
   * but `noGoogleListing` stayed true (persisted in form state).
   *
   * That left the search view rendered with a permanently disabled
   * input — the CustomAutoSearch is `disabled={data.noGoogleListing}`
   * so typing did nothing. Fix in the "Search again" handler caught
   * the in-screen case but missed the navigation-out-and-back case.
   *
   * Detection: if we're rendering the search view (!showDetailedForm)
   * AND `noGoogleListing` is true AND no real "no listing" checkbox
   * intent persisted, we treat the flag as stale from a prior manual
   * entry and reset it. We DON'T reset if the user explicitly ticked
   * the "My business is not listed here" checkbox (handled inline
   * inside its handler — that path keeps showDetailedForm=true in
   * tandem so it never reaches this branch).
   */
  useEffect(() => {
    if (showDetailedForm) return;
    if (!data.noGoogleListing) return;
    updateFields({ noGoogleListing: false });
    try {
      const aboutForm = secureStorage.get('aboutBusinessForm', {});
      aboutForm.noGoogleListing = false;
      secureStorage.set('aboutBusinessForm', aboutForm);
    } catch (_e) { /* secureStorage soft-fails */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showDetailedForm]);

  /*
   * Auto-validate website on mount / step return.
   *
   * Bug: user pre-fills businessUrl on a previous visit (validated +
   * sitemap fetched), navigates to step 1 + back. On return, the
   * url is still in form state but `websiteValidatedUrl` in
   * secureStorage may be missing OR mismatched (stale state from a
   * different agent / cleared by another flow). handleUrlVerification
   * only runs on user typing or place-pick — neither fires on a
   * pure remount, so the Next button silently treats the URL as
   * unvalidated.
   *
   * On mount, if we have a businessUrl in form state AND the user
   * isn't in "no website" mode AND the cached validated URL doesn't
   * match the current value, kick off verification once. The
   * `didAutoValidateRef` guard prevents loops if validation flips
   * the URL or errors out.
   */
  const didAutoValidateRef = useRef(false);
  useEffect(() => {
    if (didAutoValidateRef.current) return;
    /*
     * Runs for the manual/detail form TOO (it used to bail on
     * `showDetailedForm`). Reaching that form through the confirm modal's
     * Edit button remounts StepTwo, so `isUrlValid` is back to null while the
     * URL itself is still in form state — validate() then rejects Continue
     * with "Provide a valid URL or check the box below." and the user has no
     * way to clear it except retyping the URL.
     *
     * `typingTimeout.current` is only ever set by handleUrlChange, so a truthy
     * value means the user has typed in the URL field — the 800ms debounce
     * owns verification from that point and we must not race it.
     */
    if (typingTimeout.current) return;
    if (isVerifying) return;
    const currentUrl = (data.businessUrl || '').trim();
    if (!currentUrl) return;
    if (data.noBusinessWebsite) return;
    let cachedValidated = '';
    try {
      cachedValidated = secureStorage.get('websiteValidatedUrl', '') || '';
    } catch (_e) { /* secureStorage soft-fails */ }
    if (cachedValidated && cachedValidated === currentUrl) {
      // Already verified in this session (GMB pick / listing pick / earlier
      // visit) — just restore the local "verified" state instead of paying for
      // another round-trip. Skipping this left `isUrlValid` null after a
      // remount, which is what blocked Continue with a bogus URL error.
      didAutoValidateRef.current = true;
      setIsUrlValid(true);
      setErrors(prev => ({ ...prev, businessUrl: null }));
      try {
        const cachedUrls = secureStorage.get('urls', []) || [];
        const cachedSelected = secureStorage.get('selectedSiteMapUrls', []) || [];
        if (cachedUrls.length) setFilteredPages(cachedUrls);
        setSelectedSitemapCount(cachedSelected.filter(u => u.checkedStatus).length);
      } catch (_e) { /* secureStorage soft-fails */ }
      return;
    }
    didAutoValidateRef.current = true;
    // Fire-and-forget — handleUrlVerification handles its own loading
    // and error state surface via setIsUrlValid / setErrors.
    handleUrlVerification(currentUrl).catch((err) =>
      console.warn('[StepTwo] auto-validate failed:', err?.message || err),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.businessUrl, data.noBusinessWebsite, showDetailedForm]);

  const fetchGoogleResults = (text) => {

    if (!window.google?.maps?.places || !text) {
      setGoogleResults([]); setShowDropdown(false); return;
    }
    const service = new window.google.maps.places.AutocompleteService();
    service.getPlacePredictions({ input: text, types: ['establishment'] }, (predictions) => {
      const results = predictions || [];
      setGoogleResults(results);
      setShowDropdown(results.length > 0);
    });
  };

  const fetchPlaceDetails = async (placeId) => {
    if (!window.google?.maps?.places) return;
    setLoading?.(true); setIsProcessing?.(true);

    if (prevPlaceIdRef.current && prevPlaceIdRef.current !== placeId) {
      secureStorage.remove('selectedSiteMapUrls');
      secureStorage.remove('selectedSiteMapUrls_backup');
      secureStorage.remove('urls');
      secureStorage.remove('websiteValidatedUrl');
      secureStorage.set('viewSiteMap', false);
      window.dispatchEvent(new Event('sitemapSelectionUpdated'));
    }
    prevPlaceIdRef.current = placeId;

    const service = new window.google.maps.places.PlacesService(document.createElement('div'));
    service.getDetails(
      {
        placeId,
        fields: [
          'place_id', 'name', 'url', 'website', 'formatted_address',
          'international_phone_number', 'formatted_phone_number', 'opening_hours',
          'rating', 'user_ratings_total', 'business_status', 'types', 'address_components',
        ],
      },
      async (result, status) => {

        if (status === window.google.maps.places.PlacesServiceStatus.OK) {
          const addressFields = extractAddressComponents(result.address_components || []);
          const rawHours = result.opening_hours?.weekday_text || [];
          const finalHours = hasValidGmbHours(rawHours) ? rawHours.join(' | ') : getDefaultHoursString();

          const businessData = {
            businessName: result.name || '',
            address: result.formatted_address || '',
            phone: result.international_phone_number || result.formatted_phone_number || '',
            internationalPhone: result.international_phone_number || '',
            website: result.website || '',
            rating: result.rating || '',
            totalRatings: result.user_ratings_total || '',
            hours: finalHours,
            businessStatus: result.business_status || '',
            categories: result.types || [],
            address_components: result.address_components || [],
            city: addressFields.city,
            state: addressFields.state,
            state_code: addressFields.state_code,
            country: addressFields.country,
            country_code: addressFields.country_code,
            postal_code: addressFields.postal_code,
            street_number: addressFields.street_number,
            route: addressFields.route,
          };

          secureStorage.set('placeDetailsExtract', businessData);
          const normalizedWebsite = normalizeWebsite(businessData.website);

          updateFields({ placeDetails: businessData, businessUrl: normalizedWebsite || '' });

          if (addressFields.country_code) setSelectedCountry(addressFields.country_code.toLowerCase());

          if (normalizedWebsite && !data.noBusinessWebsite) {
            secureStorage.set('businessUrl', normalizedWebsite);
            const aboutForm = secureStorage.get('aboutBusinessForm', {});
            aboutForm.businessUrl = normalizedWebsite;
            aboutForm.noBusinessWebsite = false;
            secureStorage.set('aboutBusinessForm', aboutForm);
            await handleUrlVerification(normalizedWebsite);
          } else {
            secureStorage.remove('businessUrl');
            const aboutForm = secureStorage.get('aboutBusinessForm', {});
            delete aboutForm.businessUrl;
            aboutForm.noBusinessWebsite = true;
            secureStorage.set('aboutBusinessForm', aboutForm);
          }
        } else {
          console.error('Place details fetch failed:', status);
        }
        setLoading?.(false); setIsProcessing?.(false);
      }
    );
  };

  const handlePlaceSelect = async (item) => {
    updateFields({ isGMBSelected: true });

    const name = item?.structured_formatting?.main_text || item?.description || '';
    const googleLink = `https://www.google.com/search?q=${encodeURIComponent(item.description)}`;

    updateFields({ displayBusinessName: name, googleListing: googleLink, noGoogleListing: false });
    secureStorage.set('displayBusinessName', name);
    secureStorage.set('googleListing', googleLink);
    secureStorage.set('showNoGMBCheckbox', 'true');

    const aboutForm = secureStorage.get('aboutBusinessForm', {});
    aboutForm.googleListing = googleLink;
    aboutForm.noGoogleListing = false;
    delete aboutForm.noBusinessWebsite;
    secureStorage.set('aboutBusinessForm', aboutForm);

    setGoogleResults([]); setShowDropdown(false); setHighlightedIndex(-1);
    setShowNoGMBCheckbox(true);
    await fetchPlaceDetails(item.place_id);
  };

  const handleAddressChange = async (selectedAddress, placeId = null) => {
    // GA4: address entered/selected on the manual details form. The address
    // itself is business info; fire only when a non-empty value is set.
    if ((selectedAddress || '').trim()) {
      trackEvent('businessdetails_address_entered', { screen: 'business_details_manual' });
    }
    if (placeId && window.google?.maps?.places) {
      const geocoder = new window.google.maps.Geocoder();
      try {
        const result = await new Promise((resolve, reject) => {
          geocoder.geocode({ placeId }, (results, status) => {
            if (status === 'OK' && results[0]) resolve(results[0]);
            else reject(new Error('Geocoding failed'));
          });
        });
        const addressFields = extractAddressComponents(result.address_components);
        const updatedDetails = {
          ...data.placeDetails,
          address: result.formatted_address || selectedAddress,
          address_components: result.address_components,
          ...addressFields,
        };
        updateFields({ placeDetails: updatedDetails });
        secureStorage.set('placeDetailsExtract', updatedDetails);
        if (addressFields.country_code) setSelectedCountry(addressFields.country_code.toLowerCase());
      } catch (error) {
        console.error('Failed to extract address components:', error);
        updateFields({ placeDetails: { ...data.placeDetails, address: selectedAddress } });
        const placeDetails = secureStorage.get('placeDetailsExtract', {});
        placeDetails.address = selectedAddress;
        secureStorage.set('placeDetailsExtract', placeDetails);
      }
    } else {
      updateFields({ placeDetails: { ...data.placeDetails, address: selectedAddress } });
      const placeDetails = secureStorage.get('placeDetailsExtract', {});
      placeDetails.address = selectedAddress;
      secureStorage.set('placeDetailsExtract', placeDetails);
    }
    // Clear the address error live once a location is chosen.
    setErrors(prev => ({ ...prev, address: (selectedAddress || '').trim() ? null : prev.address }));
  };

  const handleUrlVerification = async (url) => {
    if (!url || isUrlEffectivelyEmpty(url)) return false;
    setIsVerifying(true); setIsUrlValid(null); setIsProcessing?.(true);
    try {
      const result = await validateWebsite(url);
      if (!result?.valid) {
        setIsUrlValid(false);
        setErrors(prev => ({ ...prev, businessUrl: 'Invalid URL' }));
        secureStorage.remove('websiteValidatedUrl');
        // NOTE: Do NOT auto-tick "I do not have a Business website" here.
        // This runs from the 800ms debounce in handleUrlChange, so a user
        // who merely pauses mid-typing (URL still incomplete) would have the
        // input wiped and the checkbox auto-selected. Just surface the error
        // and let the user finish/correct the URL.
        return false;
      }
      const finalUrl = (result?.finalUrl || url || '').trim();
      setIsUrlValid(true);
      setErrors(prev => ({ ...prev, businessUrl: null }));
      secureStorage.remove('selectedSiteMapUrls');
      secureStorage.remove('selectedSiteMapUrls_backup');
      secureStorage.set('businessUrl', finalUrl);
      secureStorage.set('websiteValidatedUrl', finalUrl);
      const aboutForm = secureStorage.get('aboutBusinessForm', {});
      aboutForm.businessUrl = finalUrl;
      aboutForm.noBusinessWebsite = false;
      secureStorage.set('aboutBusinessForm', aboutForm);

      const res = await listSiteMap(finalUrl);
      let urls = res?.urls || [];
      if (finalUrl && !urls.includes(finalUrl)) urls.unshift(finalUrl);

      // ❌ secureStorage.set('urls', urls);  ← YEH HATA DO — 5-6k urls save hoti thi

      const pages = await filterCompanyPages(urls);  // filterCompanyPages ke andar bhi sessionStorage set hota hai — woh bhi sirf 10 ka
      const fp = Array.isArray(pages) ? pages : urls;

      const first10 = fp.slice(0, 10);

      // ✅ Sirf first 10 hi save karo
      secureStorage.set('urls', first10);

      setFilteredPages(first10);
      const defaultSelected = first10.map((u) => ({ url: u, checkedStatus: true }));
      secureStorage.set('selectedSiteMapUrls', defaultSelected);
      secureStorage.set('selectedSiteMapUrls_backup', defaultSelected);
      setSelectedSitemapCount(10);
      updateFields({ sitemapUrls: first10, selectedSitemapUrls: defaultSelected });

      secureStorage.set('viewSiteMap', true);
      window.dispatchEvent(new Event('sitemapSelectionUpdated'));
      return true;
    } catch (error) {
      console.error('URL verification failed:', error);
      setIsUrlValid(false);
      setErrors(prev => ({ ...prev, businessUrl: 'Verification failed' }));
      secureStorage.remove('websiteValidatedUrl');
      secureStorage.set('viewSiteMap', false);
      return false;
    } finally {
      setIsVerifying(false); setIsProcessing?.(false);
    }
  };
  const handleUrlChange = (value) => {
    let input = value || '';
    if (input.trim() === '' || input === 'http://' || input === 'https://') {
      updateFields({ businessUrl: '' });
      setIsUrlValid(null); setFilteredPages([]);
      secureStorage.remove('urls'); secureStorage.remove('websiteValidatedUrl');
      secureStorage.set('viewSiteMap', false);
      const aboutForm = secureStorage.get('aboutBusinessForm', {});
      delete aboutForm.businessUrl;
      secureStorage.set('aboutBusinessForm', aboutForm);
      secureStorage.remove('businessUrl');
      if (typingTimeout.current) clearTimeout(typingTimeout.current);
      return;
    }
    let cleanDomain = input.replace(/^(https?:\/\/)+(www\.)?/gi, '');
    if (!cleanDomain) { updateFields({ businessUrl: '' }); return; }
    const finalUrl = `https://${cleanDomain}`;
    updateFields({ businessUrl: finalUrl });
    const aboutForm = secureStorage.get('aboutBusinessForm', {});
    if (!cleanDomain.includes('.')) {
      setIsUrlValid(null); setFilteredPages([]);
      secureStorage.remove('urls'); secureStorage.remove('websiteValidatedUrl');
      secureStorage.set('viewSiteMap', false);
      delete aboutForm.businessUrl;
      secureStorage.set('aboutBusinessForm', aboutForm);
      secureStorage.remove('businessUrl');
      return;
    }
    aboutForm.businessUrl = finalUrl;
    delete aboutForm.noBusinessWebsite;
    secureStorage.set('aboutBusinessForm', aboutForm);
    secureStorage.set('businessUrl', finalUrl);
    if (touched.businessUrl) {
      const err = validateField('businessUrl', finalUrl);
      setErrors(prev => ({ ...prev, businessUrl: err }));
    }
    if (typingTimeout.current) clearTimeout(typingTimeout.current);
    typingTimeout.current = setTimeout(() => {
      if (cleanDomain.length >= 3 && cleanDomain.includes('.')) handleUrlVerification(finalUrl);
    }, 800);
  };

  const validatePhone = (phone) => {
    if (!phone) return 'Phone number is required';
    const phoneObj = parsePhoneNumberFromString(phone);
    if (!phoneObj || !phoneObj.isValid()) return 'Please enter a valid  phone number';
    return null;
  };

  const handlePhoneChange = (phone, countryData) => {
    const fullPhone = phone.startsWith('+') ? phone : `+${phone}`;
    const countryCode = countryData?.countryCode || 'us';
    // PhoneInput's country is used only for its own flag/formatting UI —
    // do NOT let it overwrite placeDetails.country_code, which must
    // reflect the BUSINESS ADDRESS country (set by GMB pick or the
    // Google Address field). Mixing them up was causing the post-purchase
    // auto-assign to buy a number in the phone's country (e.g. CA) when
    // the business address was in a different country (e.g. US).
    setSelectedCountry(countryCode);
    updateFields({
      placeDetails: {
        ...data.placeDetails,
        phone: fullPhone,
        internationalPhone: fullPhone,
      },
    });
    const placeDetails = secureStorage.get('placeDetailsExtract', {});
    placeDetails.phone = fullPhone;
    placeDetails.internationalPhone = fullPhone;
    // country_code intentionally NOT written here — owned by address.
    secureStorage.set('placeDetailsExtract', placeDetails);
    if (touched.phone) {
      const err = validatePhone(fullPhone);
      setErrors(prev => ({ ...prev, phone: err }));
    }
  };

  const handleNoWebsiteCheckbox = (checked) => {
    updateFields({ noBusinessWebsite: checked, businessUrl: checked ? '' : data.businessUrl });
    const aboutForm = secureStorage.get('aboutBusinessForm', {});
    if (checked) {
      aboutForm.noBusinessWebsite = true;
      delete aboutForm.businessUrl;
      setIsUrlValid(null);
      secureStorage.remove('businessUrl');
      setErrors(prev => ({ ...prev, businessUrl: null }));
      /*
       * Drop the sitemap that was fetched for the URL we just disowned.
       * buildFinalPayload still reads `selectedSiteMapUrls` / `sitemapUrls`,
       * so without this the agent was trained on the website of a user who
       * had explicitly said "I do not have a Business website".
       */
      secureStorage.remove('websiteValidatedUrl');
      secureStorage.remove('urls');
      secureStorage.remove('selectedSiteMapUrls');
      secureStorage.remove('selectedSiteMapUrls_backup');
      secureStorage.set('viewSiteMap', false);
      setFilteredPages([]);
      setSelectedSitemapCount(0);
      updateFields({ sitemapUrls: [], selectedSitemapUrls: [] });
      window.dispatchEvent(new Event('sitemapSelectionUpdated'));
    } else {
      delete aboutForm.noBusinessWebsite;
    }
    secureStorage.set('aboutBusinessForm', aboutForm);
  };
  const handleManualEntryClick = () => {
    const currentBusinessName = data.placeDetails?.businessName || data.displayBusinessName || '';

    const proceedToManual = () => {
      // GA4: user chose manual entry from the "Can't find it?" banner
      // (zip / postal code didn't resolve to their location).
      trackEvent('business_listing_manual_entry', {
        source: 'zip_not_found',
      });
      setShowDetailedForm(true);

      // Always carry over the business name the user already entered (whether
      // typed in the simple view or from a GMB pick) so they don't retype it.
      const nameToKeep = currentBusinessName;

      const clearedDetails = {
        businessName: nameToKeep,
        address: '',
        phone: '',
        internationalPhone: '',
        email: '',
        website: '',
        rating: '',
        totalRatings: '',
        hours: [],
        businessStatus: '',
        categories: [],
      };

      updateFields({
        noGoogleListing: true,
        noBusinessWebsite: false,      // ✅ checkbox unchecked rakho
        displayBusinessName: nameToKeep,
        googleListing: '',
        businessUrl: '',               // ✅ website bhi clear
        placeDetails: { ...clearedDetails },
        isGMBSelected: false,
      });

      // ✅ Search field clear karo
      secureStorage.remove('googleListing');
      secureStorage.remove('displayBusinessName');
      secureStorage.remove('businessUrl');
      secureStorage.remove('websiteValidatedUrl');
      secureStorage.remove('selectedSiteMapUrls');
      secureStorage.remove('selectedSiteMapUrls_backup');
      secureStorage.remove('urls');
      secureStorage.set('viewSiteMap', false);
      secureStorage.set('placeDetailsExtract', clearedDetails);

      if (nameToKeep) {
        secureStorage.set('displayBusinessName', nameToKeep);
      }

      const aboutForm = secureStorage.get('aboutBusinessForm', {});
      aboutForm.noGoogleListing = true;
      aboutForm.noBusinessWebsite = false;  // ✅ storage mein bhi false
      delete aboutForm.googleListing;
      delete aboutForm.businessUrl;
      secureStorage.set('aboutBusinessForm', aboutForm);

      // ✅ URL validation state reset
      setIsUrlValid(null);
      setGoogleResults([]);
      setShowNoGMBCheckbox(false);
      setIsGMBSelected(false);
    };

    if (onRequestNoGMBConfirm) {
      onRequestNoGMBConfirm({
        onConfirm: proceedToManual,
        onCancel: () => updateFields({ noGoogleListing: false }),
      });
    } else {
      proceedToManual();
    }
  };

  useEffect(() => {
    const storedForm = secureStorage.get('aboutBusinessForm', {});
    const storedDetails = secureStorage.get('placeDetailsExtract', {});
    const storedGoogle = storedForm.googleListing || secureStorage.get('googleListing', '');
    if (storedGoogle) updateFields({ googleListing: storedGoogle });
    const storedName = secureStorage.get('displayBusinessName', '');
    if (storedName) { updateFields({ displayBusinessName: storedName }); setShowNoGMBCheckbox(true); }
    if (typeof storedForm.noGoogleListing === 'boolean') {
      updateFields({ noGoogleListing: storedForm.noGoogleListing });
      if (storedForm.noGoogleListing) setShowNoGMBCheckbox(true);
    }
    if (typeof storedForm.noBusinessWebsite === 'boolean') updateFields({ noBusinessWebsite: storedForm.noBusinessWebsite });
    if (!storedForm.noBusinessWebsite && storedForm.businessUrl) {
      updateFields({ businessUrl: storedForm.businessUrl });
      const validatedUrl = secureStorage.get('websiteValidatedUrl', '');
      if (validatedUrl === storedForm.businessUrl) {
        setIsUrlValid(true);
        const urls = secureStorage.get('urls', []);
        const selectedUrls = secureStorage.get('selectedSiteMapUrls', []);
        const count = selectedUrls.filter(u => u.checkedStatus).length;
        setFilteredPages(urls);
        setSelectedSitemapCount(count);
      }
    }
    if (storedDetails.country_code) setSelectedCountry(storedDetails.country_code.toLowerCase());
    if (storedDetails.businessName || storedDetails.phone || storedDetails.address) updateFields({ placeDetails: storedDetails });
    const showCheckbox = secureStorage.get('showNoGMBCheckbox', '');
    if (showCheckbox === 'true') setShowNoGMBCheckbox(true);
  }, []);

  useEffect(() => {
    if (data.displayBusinessName?.trim().length > 0) {
      setShowNoGMBCheckbox(true);
      secureStorage.set('showNoGMBCheckbox', 'true');
    }
  }, [data.displayBusinessName]);

  // ── FIX: validate mein scrollToFirstError call ───────────────
  useImperativeHandle(ref, () => ({
    // Simple (zip) view: Business Name and Zip / Postal Code are mandatory.
    // Returns true when valid, otherwise flags the inline errors and returns false.
    validateSimpleView: () => {
      const nameOk = (data.displayBusinessName || '').trim().length > 0;
      const zipOk = (data.zipCode || '').trim().length > 0;
      setNameTouched(true);
      setNameError(nameOk ? '' : 'Business name is required');
      if (!zipOk) {
        setZipTouched(true);
        setShowZipList(false); // ensure the empty-zip required error is visible
      }
      return nameOk && zipOk;
    },
    // `overrides.noBusinessWebsite` — parent has just queued the "no website"
    // checkbox but that state hasn't reached `data` yet; validate against the
    // intent, not the stale prop.
    validate: (overrides = {}) => {
      const newErrors = {};
      const newTouched = {};
      const noBusinessWebsite =
        overrides.noBusinessWebsite === true ? true : !!data.noBusinessWebsite;

      if (!showDetailedForm) {
        if (!data.noGoogleListing) {
          if (!data.placeDetails) {
            newErrors.googleListing = 'Please select your business or click "Enter Business Detail Manually"';
            newTouched.googleListing = true;
          }
          if (data.placeDetails) {
            const phoneError = validatePhone(data.placeDetails?.phone);
            if (phoneError) { newErrors.phone = phoneError; newTouched.phone = true; }
            if (!data.placeDetails?.businessName) { newErrors.businessName = 'Business name is required'; newTouched.businessName = true; }
            if (!data.placeDetails?.address) { newErrors.address = 'Address is required'; newTouched.address = true; }
            const emailError = validateEmail(data.placeDetails?.email || '');
            if (emailError) { newErrors.email = emailError; newTouched.email = true; }
            if (!noBusinessWebsite) {
              if (isUrlEffectivelyEmpty(data.businessUrl)) { newErrors.businessUrl = 'Enter a valid website URL or check below.'; newTouched.businessUrl = true; }
              else if (isVerifying) { newErrors.businessUrl = 'Please wait... Website is still validating.'; newTouched.businessUrl = true; }
              else if (isUrlValid === false) { newErrors.businessUrl = 'Invalid website URL.'; newTouched.businessUrl = true; }
            }
          }
        }
      } else {
        if (isVerifying && !noBusinessWebsite) { newErrors.businessUrl = 'Please wait... Website is still validating.'; newTouched.businessUrl = true; }
        else if (!noBusinessWebsite) {
          if (isUrlEffectivelyEmpty(data.businessUrl)) { newErrors.businessUrl = 'Provide a valid URL or select the box.'; newTouched.businessUrl = true; }
          else if (isUrlValid !== true) { newErrors.businessUrl = 'Provide a valid URL or check the box below.'; newTouched.businessUrl = true; }
        }
        try {
          const sessionSelectedUrls = secureStorage.get('selectedSiteMapUrls', []);
          const selectedCount = sessionSelectedUrls.filter(item => item.checkedStatus).length;
          if (selectedCount > MAX_SELECTED_SITEMAP_URLS) { newErrors.businessUrl = `You can select only ${MAX_SELECTED_SITEMAP_URLS} pages.`; newTouched.businessUrl = true; }
        } catch (e) { console.error('Error checking sitemap URLs', e); }
        const phoneError = validatePhone(data.placeDetails?.phone);
        if (phoneError) { newErrors.phone = phoneError; newTouched.phone = true; }
        if (!data.placeDetails?.businessName && !data.displayBusinessName) { newErrors.businessName = 'Business name is required'; newTouched.businessName = true; }
        if (!data.placeDetails?.address) { newErrors.address = 'Address is required'; newTouched.address = true; }
        const emailError = validateEmail(data.placeDetails?.email || '');
        if (emailError) { newErrors.email = emailError; newTouched.email = true; }
      }

      setErrors(newErrors);
      setTouched(newTouched);

      // ── Auto-scroll to first error ──
      if (Object.keys(newErrors).length > 0) {
        scrollToFirstError(newErrors);
      }

      const isValid = Object.keys(newErrors).length === 0;
      return isValid ? true : { isValid: false, errors: newErrors };
    }
  }));

  return (
    <div className={styles.stepContent}>

      {/* GOOGLE BUSINESS LISTING CONFIRMATION VIEW */}
      {showListingConfirm && !showDetailedForm && (
        <>
          <div className={styles.titleBar}>
            <h4 className={styles.lightTitle}>Confirm your Google</h4>
            <h5 className={styles.boldTitle}>Business Listing</h5>
            <p className={styles.subtitle}>
              We ask for <b className={styles.boldText}>Google Business Listing</b> confirmation
              to Train your AI Receptionist fast, in Real time
            </p>
          </div>

          {listingLoading ? (
            <div className={styles.listingList}>
              <div className={styles.zipNoData}>Searching businesses…</div>
            </div>
          ) : listingResults.length === 0 ? (
            <div className={styles.listingList}>
              <div className={styles.zipNoData}>
                No matching businesses found. You can enter details manually below.
              </div>
            </div>
          ) : (
            <div className={styles.listingList}>
              {listingResults.map((b, i) => (
                <div key={b.place_id || i} className={styles.listingItem}>
                  <div className={styles.listingInfo}>
                    <p className={styles.listingName}>{b.name}</p>
                    <p className={styles.listingAddr}>{b.address}</p>
                  </div>
                  <div className={styles.listingRight}>
                    {b.rating ? (
                      <p className={styles.listingRating}>
                        {b.rating} <span className={styles.listingStar}>★</span> ({b.reviews})
                      </p>
                    ) : null}
                    <button
                      type="button"
                      className={styles.useThisBtn}
                      onClick={() => onUseListing?.(b)}
                    >
                      Use this
                      {/* Inline arrow → inherits the button's text color (currentColor),
                          so it flips to white automatically on hover. */}
                      <svg className={styles.useThisArrow} viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* SIMPLE SEARCH VIEW */}
      {!showDetailedForm && !showListingConfirm && (
        <>
          <div className={styles.titleBar}>
            <h4 className={styles.lightTitle}>Enter your</h4>
            <h5 className={styles.boldTitle}>business details</h5>
            <p className={styles.subtitle}>Provide your business name and zip code.
              We'll use this information to answer calls correctly</p>
          </div>

          <div className={styles.detailForm} onClick={focusGroupInput}>


            <div>
              <div className={`${styles.formGroup} ${nameTouched && nameError ? styles.formGroupError : ''}`}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <label style={{ marginBottom: 0 }}>Your Business Name <span style={{ color: '#e53935' }}>*</span></label>

                </div>
                <input
                  placeholder="Enter your business name here"
                  value={data.displayBusinessName || ''}
                  onChange={(e) => {
                    updateFields({ displayBusinessName: e.target.value });
                    // Clear the required error live once a name is typed.
                    if (nameTouched) setNameError(e.target.value.trim() ? '' : 'Business name is required');
                  }}
                  onBlur={(e) => {
                    setNameTouched(true);
                    const typedName = e.target.value.trim();
                    setNameError(typedName ? '' : 'Business name is required');
                    // GA4: user finished entering their business name (field
                    // left with a non-empty value). The one-shot guard in
                    // trackEvent drops repeats of the same name.
                    if (typedName) {
                      trackEvent('business_name_entered', { business_name: typedName });
                    }
                  }}
                  maxLength={50}
                />
              </div>
              {nameTouched && nameError && (
                <p className={styles.fieldError}>{nameError}</p>
              )}
            </div>
            <div ref={zipWrapRef}>
              <div className={`${styles.formGroup} ${(zipTouched && !(data.zipCode || '').trim()) || zipNotFound ? styles.formGroupError : ''}`}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <label style={{ marginBottom: 0 }}>Zip / Postal Code <span style={{ color: '#e53935' }}>*</span></label>
                </div>
                <input
                  ref={zipInputRef}
                  type="text"
                  inputMode={zipInputMode}
                  placeholder="Enter zip / postal code"
                  value={data.zipCode || ''}
                  onChange={(e) => handleZipChange(e.target.value)}
                  onBlur={() => setZipTouched(true)}
                  maxLength={6}
                  autoComplete="off"
                />
                {/* Loading spinner while searching, clear (✕) once a location is picked. */}
                {zipLoading && <div className={`newloader ${styles.zipSpinner}`} />}
                {!zipLoading && data.zipLocation && (
                  <button
                    type="button"
                    className={styles.zipClearBtn}
                    onClick={clearZip}
                    aria-label="Clear location"
                  >
                    ✕
                  </button>
                )}
              </div>

              {/* Empty-required error */}
              {zipTouched && !(data.zipCode || '').trim() && !showZipList && (
                <p className={styles.fieldError}>Please enter your zip / postal code.</p>
              )}

              {/* No location matched — Continue is blocked; user must fix the
                  code or use the "Enter manually" option below. */}
              {zipNotFound && (data.zipCode || '').trim() && (
                <p className={styles.fieldError}>
                  We couldn't find this location. Please check the code or enter your details manually.
                </p>
              )}

              {/* From the 2nd wrong try onward, surface the manual-entry path
                  right below the pin field. */}
              {zipNotFound && zipWrongCount >= 2 && (
                <>
                  <div className={styles.orDivider}><span>OR</span></div>
                  <div className={styles.manualFooterCard}>
                    <div className={styles.manualFooterText}>
                      <p className={styles.manualFooterTitle}>Can't find it?</p>
                      <p className={styles.manualFooterSub}>Enter details manually instead</p>
                    </div>
                    <button
                      type="button"
                      className={styles.manualFooterBtn}
                      onClick={handleManualEntryClick}
                    >
                      Enter manually
                      <img className={styles.arrow} src="/svg/arrow_2_new.svg" alt="" />
                    </button>
                  </div>
                </>
              )}

              {/* Selected location summary */}
              {data.zipLocation && !showZipList && (
                <p className={styles.zipSelectedLine}>
                  {[data.zipLocation.zip, data.zipLocation.city, data.zipLocation.state, data.zipLocation.country]
                    .filter(Boolean)
                    .join(', ')}
                </p>
              )}

              {/* Location dropdown removed — zip / postal code is entered directly. */}

              {/* Hint — shown only before the user engages */}
              {!showZipList && !data.zipLocation && !zipNotFound && (
                <div className={styles.infoZip}>
                  <img src="/svg/informtion-icon 2.svg" alt="informtion-icon 2" />
                  <p>Type your zip / postal code to find your business location. </p>
                </div>
              )}
            </div>
          </div>


        </>
      )}

      {/* MANUAL ENTRY FORM */}
      {showDetailedForm && (
        <>
          <div className={styles.titleBar}>
            <h4 className={styles.lightTitle}>Update Your </h4>
            <h5 className={styles.boldTitle}>Business Details</h5>
            <p className={styles.subtitle}>
              {data.placeDetails ? 'Keep your information up to date so customers can finds and trust your business.' : 'Enter your business details manually'}
            </p>
          </div>

          <div className={styles.detailForm} onClick={focusGroupInput}>

            {/* ── Website URL ── */}
            <div className={`${styles.cardInputContainer} ${touched.businessUrl && errors.businessUrl ? styles.cardInputError : ""}`} ref={fieldRefs.businessUrl} data-field-wrapper>
              <label className={styles.cardInputLabel}>Your Website (URL)</label>
              <div className={styles.inputWithIcon}>
                <input
                  className={styles.cardInputControl}
                  placeholder="https://www.example.com"
                  value={data.noBusinessWebsite ? '' : (data.businessUrl || '')}
                  onChange={(e) => handleUrlChange(e.target.value)}
                  onBlur={() => {
                    setTouched(prev => ({ ...prev, businessUrl: true }));
                    // GA4: website URL entered on the manual details form.
                    if ((data.businessUrl || '').trim()) {
                      trackEvent('businessdetails_website_entered', { screen: 'business_details_manual' });
                    }
                  }}
                  disabled={data.noBusinessWebsite}
                />
                {isVerifying && <div className={`newloader ${styles.successIcon}`}></div>}
                {!isVerifying && data.businessUrl && isUrlValid === true && (
                  <span className={styles.successIcon}><img src="/svg/check-icon.svg" alt="valid" /></span>
                )}
                {!isVerifying && data.businessUrl && isUrlValid === false && (
                  <span className={styles.successIcon}><img src="/svg/cross-icon.svg" alt="invalid" /></span>
                )}
              </div>
              {data.businessUrl ? touched.businessUrl && errors.businessUrl && <p className={styles.errorText}>{errors.businessUrl}</p> : ""}
            </div>

            {/* No Website Checkbox */}
            <div className={styles.chekboxDiv2} style={{ marginTop: '-8px', marginBottom: '20px' }}>
              <label className={styles.customCheckbox}>
                <input
                  type="checkbox"
                  checked={data.noBusinessWebsite || false}
                  onChange={(e) => {
                    handleNoWebsiteCheckbox(e.target.checked);
                    // GA4: "I do not have a Business website" toggled.
                    trackEvent('businessdetails_no_website_toggled', {
                      screen: 'business_details_manual',
                      no_website: e.target.checked,
                    });
                  }}
                  disabled={isVerifying}
                />
                <span className={styles.crossmark}></span>
                <p className={styles.checkLable}>I don't have a website</p>
              </label>
            </div>

            {/* ── Business Name ── */}
            <div className={`${styles.cardInputContainer} ${touched.businessName && errors.businessName ? styles.cardInputError : ""}`} ref={fieldRefs.businessName} data-field-wrapper>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                <label className={styles.cardInputLabel} style={{ marginBottom: 0 }}>Business Name</label>
                {!data.noGoogleListing && (
                  <span
                    style={{ fontSize: '13px', color: '#6840d5', cursor: 'pointer', textDecoration: 'underline' }}
                    onClick={() => {
                      setShowDetailedForm(false);
                      updateFields({ noGoogleListing: false });
                      try {
                        const aboutForm = secureStorage.get(
                          'aboutBusinessForm',
                          {},
                        );
                        aboutForm.noGoogleListing = false;
                        secureStorage.set('aboutBusinessForm', aboutForm);
                      } catch (_e) { /* secureStorage soft-fails */ }
                    }}
                  >
                    Search again
                  </span>
                )}
              </div>
              <input
                className={styles.cardInputControl}
                placeholder="Your Business Name"
                value={data.placeDetails?.businessName || data.displayBusinessName || ''}
                onChange={(e) => {
                  const value = e.target.value.replace(/[0-9]/g, '').replace(/([\u2700-\u27BF]|[\uE000-\uF8FF]|[\uD83C-\uDBFF\uDC00-\uDFFF])/g, '');
                  updateFields({ displayBusinessName: value, placeDetails: { ...data.placeDetails, businessName: value } });
                  const placeDetails = secureStorage.get('placeDetailsExtract', {});
                  placeDetails.businessName = value;
                  secureStorage.set('placeDetailsExtract', placeDetails);
                  // Clear the error live as the user types a valid name.
                  setErrors(prev => ({ ...prev, businessName: value.trim() ? null : prev.businessName }));
                }}
                onBlur={() => {
                  setTouched(prev => ({ ...prev, businessName: true }));
                  const value = (data.placeDetails?.businessName || data.displayBusinessName || '').trim();
                  setErrors(prev => ({ ...prev, businessName: value ? null : 'Business name is required' }));
                  // GA4: business name entered on the manual details form.
                  if (value) {
                    trackEvent('businessdetails_name_entered', { screen: 'business_details_manual' });
                  }
                }}
                maxLength={50}
              />
              {touched.businessName && errors.businessName && <p className={styles.errorText}>{errors.businessName}</p>}
            </div>

            {/* ── Phone Number ── */}
            <div className={`${styles.cardInputContainer} ${touched.phone && errors.phone ? styles.cardInputError : ""}`} ref={fieldRefs.phone} data-field-wrapper>
              <label className={styles.cardInputLabel}>Business Phone Number</label>
              <div className={styles.cardInputPhoneWrap}>
                <PhoneInput
                  country={selectedCountry}
                  value={data.placeDetails?.phone || ''}
                  onChange={(phone, countryData) => handlePhoneChange(phone, countryData)}
                  onBlur={() => {
                    setTouched(prev => ({ ...prev, phone: true }));
                    // GA4: phone number entered on the manual details form.
                    if ((data.placeDetails?.phone || '').trim()) {
                      trackEvent('businessdetails_phone_entered', {
                        screen: 'business_details_manual',
                        country: selectedCountry || '',
                      });
                    }
                  }}
                  placeholder="88XX 77X X55"
                />
              </div>
              {touched.phone && errors.phone && <p className={styles.errorText}>{errors.phone}</p>}
            </div>

            {/* ── Address ── */}
            <div
              className={`${styles.cardInputContainer} ${touched.address && errors.address ? styles.cardInputError : ""}`}
              ref={fieldRefs.address}
              data-field-wrapper
              onFocusCapture={scrollAddressIntoView}
            >
              <label className={styles.cardInputLabel}>Address</label>
              <GoogleAddressInputWithDropdown
                ref={addressInputRef}
                value={data.placeDetails?.address || ''}
                onChange={handleAddressChange}
                placeholder="123 king street"
                maxLength={300}
                inputClassName={styles.cardInputControl}
              />
              {touched.address && errors.address && <p className={styles.errorText}>{errors.address}</p>}
            </div>

            {/* ── Business Email ── */}
            <div className={`${styles.cardInputContainer} ${touched.email && errors.email ? styles.cardInputError : ""}`} ref={fieldRefs.email} data-field-wrapper>
              <label className={styles.cardInputLabel}>Business Email</label>
              <input
                className={styles.cardInputControl}
                type="email"
                placeholder="Business Email Address"
                value={data.placeDetails?.email || ''}
                maxLength={BUSINESS_EMAIL_MAX_LENGTH}
                onChange={(e) => {
                  const value = e.target.value.slice(0, BUSINESS_EMAIL_MAX_LENGTH);
                  updateFields({ placeDetails: { ...data.placeDetails, email: value } });
                  const placeDetails = secureStorage.get('placeDetailsExtract', {});
                  placeDetails.email = value;
                  secureStorage.set('placeDetailsExtract', placeDetails);
                  if (touched.email) { const err = validateEmail(value); setErrors(prev => ({ ...prev, email: err })); }
                }}
                onBlur={() => {
                  setTouched(prev => ({ ...prev, email: true }));
                  const err = validateEmail(data.placeDetails?.email || '');
                  setErrors(prev => ({ ...prev, email: err }));
                  // GA4: valid business email entered on the manual details form.
                  if ((data.placeDetails?.email || '').trim() && !err) {
                    trackEvent('businessdetails_email_entered', { screen: 'business_details_manual' });
                  }
                }}
              />
              {touched.email && errors.email && <p className={styles.errorText}>{errors.email}</p>}
            </div>

          </div>
        </>
      )}
    </div>
  );
});

export default StepTwo;
